import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "@/shared/utils/style.util";

type NativeTextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "rows"
>;

type TextareaProps = {
  autosize?: boolean
  minRows?: number
  maxRows?: number
  invalid?: boolean
} & NativeTextareaProps;

/**
 * Atelier Textarea — symmetric rounded, resizes from minRows to maxRows.
 *
 * Default: autosize true, min 1 row, max 6 rows. Used by ChatComposer.
 * Radius is always symmetric (locked rule).
 */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      autosize = true,
      minRows = 1,
      maxRows = 6,
      invalid = false,
      className,
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    const resize = useCallback(() => {
      const el = innerRef.current;
      if (el === null || !autosize) {
        return;
      }
      const computed = window.getComputedStyle(el);
      const lineHeightRaw = computed.lineHeight;
      const lineHeight = lineHeightRaw === "normal"
        ? parseFloat(computed.fontSize) * 1.4
        : parseFloat(lineHeightRaw);
      const paddingTop = parseFloat(computed.paddingTop);
      const paddingBottom = parseFloat(computed.paddingBottom);
      const minHeight = lineHeight * minRows + paddingTop + paddingBottom;
      const maxHeight = lineHeight * maxRows + paddingTop + paddingBottom;

      el.style.height = "auto";
      const next = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
      el.style.height = `${next}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
    }, [autosize, minRows, maxRows]);

    useEffect(() => {
      resize();
    }, [value, defaultValue, resize]);

    return (
      <textarea
        ref={innerRef}
        value={value}
        defaultValue={defaultValue}
        aria-invalid={invalid ? true : undefined}
        rows={minRows}
        onChange={(event) => {
          onChange?.(event);
          resize();
        }}
        className={cn(
          "w-full resize-none",
          "bg-atelier-surface-raised",
          "text-atelier-ink-primary",
          "placeholder:text-atelier-ink-muted",
          "rounded-atelier-md",
          "border border-atelier-border",
          "px-4 py-3 text-sm leading-6",
          "transition-colors duration-[120ms] ease-out",
          "atelier-focus-ring",
          "disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-atelier-surface-muted",
          "focus:border-atelier-brand",
          invalid && "border-atelier-danger focus:border-atelier-danger",
          className,
        )}
        {...props}
      />
    );
  },
);

Textarea.displayName = "Atelier.Textarea";

export { Textarea };
export type { TextareaProps };
